import type { FetchShipmentsReponse } from 'fetchers/shipment.fetchers';
import type {
  DemoParcel,
  DemoShipment,
} from 'interfaces/DemoShipment.interface';

/**
 * The exact strings the demo backend stores in place of an EDD. It computes no
 * date; substituting them is the widget's job. They sit on six fields:
 * `expected_delivery`, `expected_delivery_to`, and the four
 * `parcel.parcel_expected_time_*`.
 */
export const DEMO_EDD_FROM_TOKEN = '__DEMO_EDD_FROM__';
export const DEMO_EDD_TO_TOKEN = '__DEMO_EDD_TO__';

/**
 * One resolved delivery window, ISO strings. `null` clears all six fields.
 *
 * Both values must end `23:59:59Z`. `generateWithTimezone` takes its
 * un-timezoned branch only for that suffix; a demo carries no PTP config
 * timezone, so any other form resolves to `undefined` and `formatDeliveryDate`
 * throws on it, blanking the delivery date. The suffix also keeps
 * `shouldShowTimeDisplay` on its date-only branch, which is the display the
 * data sheet asks for.
 */
export interface DemoEddWindow {
  from: string;
  to: string;
}

const windowAt = (
  edd: DemoEddWindow | DemoEddWindow[] | null,
  index: number,
): DemoEddWindow | null => (Array.isArray(edd) ? edd[index] ?? null : edd);

/**
 * Replaces `token` with `value` when the field holds exactly that token.
 * A `null` window clears the field. A field already carrying a real date is
 * left alone, so substitution is opt-in and idempotent.
 */
const applyToken = (
  field: string | null,
  token: string,
  value: string | null,
): string | null => {
  if (value === null) return null;
  return field === token ? value : field;
};

const resolveEdd = (
  shipment: DemoShipment,
  eddWindow: DemoEddWindow | null,
): DemoShipment => {
  const from = eddWindow === null ? null : eddWindow.from;
  const to = eddWindow === null ? null : eddWindow.to;
  const { parcel } = shipment;

  const resolvedParcel: DemoParcel = {
    ...parcel,
    parcel_expected_time_first_start: applyToken(
      parcel.parcel_expected_time_first_start,
      DEMO_EDD_FROM_TOKEN,
      from,
    ),
    parcel_expected_time_latest_start: applyToken(
      parcel.parcel_expected_time_latest_start,
      DEMO_EDD_FROM_TOKEN,
      from,
    ),
    parcel_expected_time_first_end: applyToken(
      parcel.parcel_expected_time_first_end,
      DEMO_EDD_TO_TOKEN,
      to,
    ),
    parcel_expected_time_latest_end: applyToken(
      parcel.parcel_expected_time_latest_end,
      DEMO_EDD_TO_TOKEN,
      to,
    ),
  };

  return {
    ...shipment,
    expected_delivery: applyToken(
      shipment.expected_delivery,
      DEMO_EDD_FROM_TOKEN,
      from,
    ),
    expected_delivery_to: applyToken(
      shipment.expected_delivery_to,
      DEMO_EDD_TO_TOKEN,
      to,
    ),
    parcel: resolvedParcel,
  };
};

/**
 * Wraps a demo payload in the envelope `fetchShipments` returns, so
 * `getShipments` maps and post-processes it through the production path with no
 * knowledge that it is a demo.
 *
 * Carries every element: dropping the second shipment would remove what the
 * shipment toggle switches between, and that toggle is a published tour target.
 *
 * The token is an empty string, not null. It is stored for consumer validation
 * on the return path, which a demo never reaches; `null` would be
 * indistinguishable from a failed live search.
 *
 * `edd` is supplied by the caller, never computed here — the value depends on
 * the stage, the shipment index and whether the prospect changed location on
 * the product page, none of which this repo can see. One window applies to
 * every element; an array applies by index (shipment 2 trails shipment 1 by a
 * day); `null` clears the six fields, which is the Delivered stage. Omitted
 * leaves the tokens in place.
 */
export const toShipmentsResponse = (
  payload: DemoShipment | DemoShipment[],
  edd?: DemoEddWindow | DemoEddWindow[] | null,
): FetchShipmentsReponse => ({
  data: {
    shipments: (Array.isArray(payload) ? payload : [payload]).map(
      (shipment, index) =>
        edd === undefined
          ? shipment
          : resolveEdd(shipment, windowAt(edd, index)),
    ),
    token: '',
  },
});
